/*
 * Журнал сделок на стороне клиента.
 *
 * Здесь только состояние и запросы: ни одной формулы. Всё, что считается,
 * считает сервер — иначе те же правила пришлось бы держать в двух местах,
 * и однажды они разошлись бы на копейку, а потом на тысячу.
 */

import type {
	TradingAccount,
	TradingPosition,
	TradingStats,
	TradingTradeInput
} from '@andresot/protocol/trading';
import * as api from '$api/trading';
import { reportError } from '$utils/apiError';
import { toasts } from './toasts.svelte';

class TradingStore {
	accounts = $state<TradingAccount[]>([]);
	positions = $state<TradingPosition[]>([]);
	stats = $state<TradingStats | null>(null);
	accountId = $state<string | null>(null);
	loading = $state(false);
	busy = $state(false);
	private lifecycle = 0;
	private accountsRequest = 0;
	private accountRequest = 0;

	reset(): void {
		this.lifecycle += 1;
		this.accountsRequest += 1;
		this.accountRequest += 1;
		this.accounts = [];
		this.positions = [];
		this.stats = null;
		this.accountId = null;
		this.loading = false;
		this.busy = false;
	}

	get account(): TradingAccount | null {
		return this.accounts.find((item) => item.id === this.accountId) ?? null;
	}

	get openPositions(): TradingPosition[] {
		return this.positions.filter((position) => position.closedAt === null);
	}

	get closedPositions(): TradingPosition[] {
		return this.positions.filter((position) => position.closedAt !== null);
	}

	async load(): Promise<void> {
		const lifecycle = this.lifecycle;
		const request = ++this.accountsRequest;
		const selection = this.accountRequest;
		const current = () => lifecycle === this.lifecycle && request === this.accountsRequest;
		this.loading = true;
		try {
			const { accounts } = await api.fetchAccounts();
			if (!current()) return;
			this.accounts = accounts;
			if (!accounts.some((item) => item.id === this.accountId)) {
				this.accountId = accounts[0]?.id ?? null;
				this.positions = [];
				this.stats = null;
			}
			await this.reloadAccountData();
		} catch (error) {
			if (current()) reportError(error, 'Не удалось загрузить журнал сделок');
		} finally {
			if (current() && selection === this.accountRequest) this.loading = false;
		}
	}

	async selectAccount(accountId: string): Promise<void> {
		this.accountId = accountId;
		this.positions = [];
		this.stats = null;
		try {
			await this.reloadAccountData();
		} catch (error) {
			reportError(error, 'Не удалось загрузить счёт');
		}
	}

	private async reloadAccountData(): Promise<void> {
		const lifecycle = this.lifecycle;
		const request = ++this.accountRequest;
		const accountId = this.accountId;
		const current = () =>
			lifecycle === this.lifecycle &&
			request === this.accountRequest &&
			accountId === this.accountId;
		if (!accountId) {
			this.positions = [];
			this.stats = null;
			this.loading = false;
			return;
		}
		this.loading = true;
		try {
			const [positions, stats] = await Promise.all([
				api.fetchPositions(accountId),
				api.fetchStats(accountId)
			]);
			if (!current()) return;
			this.positions = positions.positions;
			this.stats = stats.stats;
		} catch (error) {
			if (current()) throw error;
		} finally {
			if (current()) this.loading = false;
		}
	}

	/** Обёртка для действий: одно место для «занят» и для показа ошибки. */
	private async run(
		action: (current: () => boolean) => Promise<void>,
		success?: string
	): Promise<boolean> {
		if (this.busy) return false;
		const lifecycle = this.lifecycle;
		const current = () => lifecycle === this.lifecycle;
		this.busy = true;
		try {
			await action(current);
			if (!current()) return false;
			if (success) toasts.success(success);
			return true;
		} catch (error) {
			if (current()) reportError(error, 'Не удалось выполнить действие');
			return false;
		} finally {
			if (current()) this.busy = false;
		}
	}

	async createAccount(input: {
		title: string;
		currency: string;
		startingBalance: string;
	}): Promise<boolean> {
		return this.run(async (current) => {
			const { accounts } = await api.createAccount(input);
			if (!current()) return;
			this.accountsRequest += 1;
			this.accounts = accounts;
			this.accountId = accounts[accounts.length - 1]?.id ?? null;
			await this.reloadAccountData();
		}, 'Счёт создан');
	}

	async updateAccount(
		accountId: string,
		input: { title?: string; currency?: string; startingBalance?: string }
	): Promise<boolean> {
		return this.run(async (current) => {
			const { accounts } = await api.updateAccount(accountId, input);
			if (!current()) return;
			this.accountsRequest += 1;
			this.accounts = accounts;
			await this.reloadAccountData();
		}, 'Счёт изменён');
	}

	async deleteAccount(accountId: string): Promise<boolean> {
		return this.run(async (current) => {
			const { accounts } = await api.deleteAccount(accountId);
			if (!current()) return;
			this.accountsRequest += 1;
			this.accounts = accounts;
			if (this.accountId === accountId) this.accountId = accounts[0]?.id ?? null;
			await this.reloadAccountData();
		}, 'Счёт удалён');
	}

	async openPosition(input: {
		symbol: string;
		targetPrice?: string;
		stopPrice?: string;
		note?: string;
		tags?: string[];
		trade: TradingTradeInput;
	}): Promise<boolean> {
		const accountId = this.accountId;
		if (!accountId) return false;
		return this.run(async (current) => {
			await api.openPosition(accountId, input);
			if (current()) await this.refresh();
		}, 'Сделка записана');
	}

	async addTrade(positionId: string, input: TradingTradeInput): Promise<boolean> {
		return this.run(async (current) => {
			await api.addTrade(positionId, input);
			if (current()) await this.refresh();
		}, 'Исполнение добавлено');
	}

	async updatePosition(
		positionId: string,
		input: Parameters<typeof api.updatePosition>[1],
		success?: string
	): Promise<boolean> {
		return this.run(async (current) => {
			const { position } = await api.updatePosition(positionId, input);
			if (!current()) return;
			const index = this.positions.findIndex((item) => item.id === positionId);
			if (index >= 0) this.positions[index] = position;
			// Итог счёта зависит от плавающей прибыли — перечитываем и его.
			await this.reloadAccounts();
		}, success);
	}

	async deletePosition(positionId: string): Promise<boolean> {
		return this.run(async (current) => {
			await api.deletePosition(positionId);
			if (current()) await this.refresh();
		}, 'Сделка удалена');
	}

	async deleteTrade(tradeId: string): Promise<boolean> {
		return this.run(async (current) => {
			await api.deleteTrade(tradeId);
			if (current()) await this.refresh();
		}, 'Исполнение удалено');
	}

	/** Перечитать всё, что зависит от сделок: счета, позиции и статистику. */
	private async refresh(): Promise<void> {
		if (await this.reloadAccounts()) await this.reloadAccountData();
	}

	private async reloadAccounts(): Promise<boolean> {
		const lifecycle = this.lifecycle;
		const request = ++this.accountsRequest;
		try {
			const { accounts } = await api.fetchAccounts();
			if (lifecycle !== this.lifecycle || request !== this.accountsRequest) return false;
			this.accounts = accounts;
			return true;
		} catch (error) {
			if (lifecycle === this.lifecycle && request === this.accountsRequest) throw error;
			return false;
		}
	}
}

export const trading = new TradingStore();
